import { useState, useRef } from "react";
import { Camera, ImageUp, Loader2, X } from "lucide-react";
import { api, photoSrc } from "../lib/api";

async function compressImage(file, maxSide = 1280, quality = 0.7) {
  return new Promise((resolve) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => { img.src = e.target.result; };
    img.onload = () => {
      let { width, height } = img;
      const scale = Math.min(1, maxSide / Math.max(width, height));
      width = Math.round(width * scale);
      height = Math.round(height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
    };
    reader.readAsDataURL(file);
  });
}

export function PhotoUploader({ photoId, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const cameraRef = useRef();
  const galleryRef = useRef();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const compressed = await compressImage(file);
      const fd = new FormData();
      fd.append("file", compressed, "foto.jpg");
      const { data } = await api.post("/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.photo_id, data.public_url);
    } catch (e) {
      setError("No se pudo subir la imagen. Puedes intentar desde galería o continuar sin foto.");
    } finally {
      setUploading(false);
    }
  };

  const src = photoId ? photoSrc(`/api/files/public/${photoId}`) : null;

  if (src) {
    return (
      <div className="relative w-full max-w-xs">
        <img src={src} alt="Foto" className="w-full rounded-xl border border-[#E2E8F0]" />
        <button type="button" onClick={() => onUploaded(null, null)} data-testid="photo-remove-btn"
          className="absolute top-2 right-2 w-9 h-9 rounded-full bg-white/90 border border-[#E2E8F0] flex items-center justify-center">
          <X className="w-4 h-4 text-[#DC2626]" />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* capture attribute hints mobile browsers to open the camera; falls back to file picker on desktop */}
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={handleFile} className="hidden" data-testid="photo-camera-input" />
      <input ref={galleryRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="photo-gallery-input" />

      {uploading ? (
        <div className="w-full h-32 rounded-xl border-2 border-dashed border-[#CBD5E1] bg-[#F8FAFC] flex flex-col items-center justify-center gap-2 text-[#475569]">
          <Loader2 className="w-7 h-7 animate-spin" />
          <span className="text-sm font-medium">Procesando y subiendo…</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button type="button" onClick={() => cameraRef.current?.click()} data-testid="photo-camera-btn"
            className="h-28 rounded-xl border-2 border-dashed border-[#CBD5E1] bg-[#F8FAFC] flex flex-col items-center justify-center gap-2 text-[#475569] hover:border-[#1E3A8A]">
            <Camera className="w-7 h-7" />
            <span className="text-sm font-medium">Tomar foto</span>
          </button>
          <button type="button" onClick={() => galleryRef.current?.click()} data-testid="photo-gallery-btn"
            className="h-28 rounded-xl border-2 border-dashed border-[#CBD5E1] bg-[#F8FAFC] flex flex-col items-center justify-center gap-2 text-[#475569] hover:border-[#1E3A8A]">
            <ImageUp className="w-7 h-7" />
            <span className="text-sm font-medium">Subir desde galería</span>
          </button>
        </div>
      )}
      <p className="text-xs text-[#94A3B8]">La imagen se comprime en tu dispositivo para ahorrar datos. Si no puedes o no es seguro tomar una foto, puedes continuar sin foto.</p>
      {error && <p className="text-sm text-[#DC2626] mt-1">{error}</p>}
    </div>
  );
}
